import { Box, Stack, Typography } from '@mui/material'
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import type { RootState } from 'store'
import SingleColumnLayout from '../../layouts/single-column-layout/single-column-layout'
import FleetDashboardMetrics from './components/FleetDashboardMetrics'
import {
  useGetDashboardSummaryQuery,
  useGetRecentDashboardOrdersQuery,
  useGetRecentDashboardTripsQuery,
} from 'store/dashboard/dashboard.api'

const greetingForHour = () => {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

const displayNameFromAuth = (email?: string, companyName?: string) => {
  if (email?.includes('@')) {
    const local = email.split('@')[0]
    const name = local.split(/[._-]/)[0]
    if (name) return name.charAt(0).toUpperCase() + name.slice(1)
  }
  if (companyName) return companyName.split(/\s+/)[0]
  return 'there'
}

const Home = () => {
  const navigate = useNavigate()
  const user = useSelector((s: RootState) => s.auth.user)
  const { data, isLoading } = useGetDashboardSummaryQuery()
  const { data: recentOrders = [], isLoading: recentOrdersLoading } = useGetRecentDashboardOrdersQuery()
  const { data: recentTrips = [], isLoading: recentTripsLoading } = useGetRecentDashboardTripsQuery()

  useEffect(() => {
    if (!window.sessionStorage.getItem('accessToken')) {
      navigate('/auth/login')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <SingleColumnLayout>
      <Box
        sx={{
          /* Viewport below sticky topbar (50px) + main Container top padding (8px) */
          height: 'calc(100vh - 58px)',
          maxHeight: 'calc(100vh - 58px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          bgcolor: '#EDEFF2',
          /* Negate Paper's px:2 / pb:2 / pt:{lg:2} so this fills edge-to-edge.
             FleetDashboardMetrics applies its own px:2 internally. */
          mx: -2,
          mb: -2,
          mt: { xs: 0, lg: -2 },
          px: 0,
          pt: 2,
          pb: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, pb: 1.5 }}
        >
          <Typography
            sx={{
              fontSize: { xs: 18, md: 20 },
              fontWeight: 800,
              color: '#0F172A',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            {greetingForHour()}, {displayNameFromAuth(user?.email, user?.companyName)} 👋
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 2,
              border: '1px solid #E8ECF2',
              bgcolor: '#fff',
            }}
          >
            <CalendarTodayOutlined sx={{ fontSize: 14, color: '#64748B' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
              {todayLabel}
            </Typography>
          </Stack>
        </Stack>
        <FleetDashboardMetrics
          loading={isLoading}
          metrics={data?.dashboardMetrics}
          recentOrders={recentOrders}
          recentTrips={recentTrips}
          recentOrdersLoading={recentOrdersLoading}
          recentTripsLoading={recentTripsLoading}
        />
      </Box>
    </SingleColumnLayout>
  )
}

export default Home
